/**
 * Custom Select
 * Replaces jQuery custom select with vanilla ES module.
 * Wraps native <select> with styled dropdown, keyboard accessible.
 */

function createCustomSelect(select) {
  // Skip selects that are already enhanced
  if (select.classList.contains('select-hidden')) return;

  select.classList.add('select-hidden');

  // Create wrapper
  const wrapper = document.createElement('div');
  wrapper.className = 'select input-group-field';
  select.parentNode.insertBefore(wrapper, select);
  wrapper.appendChild(select);

  // Create styled display
  const styled = document.createElement('div');
  styled.className = 'select-styled';
  styled.setAttribute('tabindex', '0');
  styled.setAttribute('role', 'combobox');
  styled.setAttribute('aria-expanded', 'false');
  styled.setAttribute('aria-haspopup', 'listbox');
  wrapper.appendChild(styled);

  // Create options list
  const list = document.createElement('ul');
  list.className = 'select-options';
  list.setAttribute('role', 'listbox');
  wrapper.appendChild(list);

  // Populate options
  const options = Array.from(select.options);
  options.forEach((option, index) => {
    const li = document.createElement('li');
    li.textContent = option.textContent;
    li.dataset.value = option.value;
    li.setAttribute('role', 'option');
    li.setAttribute('tabindex', '-1');

    if (index === select.selectedIndex) {
      li.classList.add('is-selected');
      li.setAttribute('aria-selected', 'true');
    }

    list.appendChild(li);
  });

  // Set initial display text
  styled.textContent = options[select.selectedIndex]?.textContent || '';

  // Toggle dropdown
  function openDropdown() {
    // Close any other open selects first
    document.querySelectorAll('.select-styled.active').forEach((el) => {
      if (el !== styled) {
        el.classList.remove('active');
        el.setAttribute('aria-expanded', 'false');
        el.nextElementSibling?.style.removeProperty('display');
      }
    });

    styled.classList.toggle('active');
    const isOpen = styled.classList.contains('active');
    styled.setAttribute('aria-expanded', String(isOpen));
    list.style.display = isOpen ? '' : 'none';

    if (isOpen) {
      // Focus selected item
      const selected = list.querySelector('.is-selected') || list.firstElementChild;
      selected?.focus();
    }
  }

  function closeDropdown() {
    styled.classList.remove('active');
    styled.setAttribute('aria-expanded', 'false');
    list.style.display = 'none';
  }

  function selectOption(li) {
    styled.textContent = li.textContent;
    styled.classList.remove('active');
    styled.setAttribute('aria-expanded', 'false');
    list.style.display = 'none';

    // Update native select
    select.value = li.dataset.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));

    // Update aria-selected
    list.querySelectorAll('li').forEach((item) => {
      item.classList.remove('is-selected');
      item.setAttribute('aria-selected', 'false');
    });
    li.classList.add('is-selected');
    li.setAttribute('aria-selected', 'true');
  }

  // Event listeners
  styled.addEventListener('click', (e) => {
    e.stopPropagation();
    openDropdown();
  });

  list.addEventListener('click', (e) => {
    const li = e.target.closest('li');
    if (!li) return;
    e.stopPropagation();
    selectOption(li);
  });

  // Keyboard support
  styled.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDropdown();
    } else if (e.key === 'Escape') {
      closeDropdown();
    }
  });

  list.addEventListener('keydown', (e) => {
    const items = Array.from(list.children);
    const current = document.activeElement;
    const index = items.indexOf(current);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        items[Math.min(index + 1, items.length - 1)]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        items[Math.max(index - 1, 0)]?.focus();
        break;
      case 'Enter':
        e.preventDefault();
        if (current?.tagName === 'LI') selectOption(current);
        break;
      case 'Escape':
        e.preventDefault();
        closeDropdown();
        styled.focus();
        break;
    }
  });

  // Close on outside click
  document.addEventListener('click', () => {
    closeDropdown();
  });

  // Initially hidden
  list.style.display = 'none';
}

function init() {
  document.querySelectorAll('select').forEach(createCustomSelect);
}

// Auto-init
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
